import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useDeleteFlow, useSaveFlow } from '../../components/confirm';
import { useHotkeys, formatCombo, effectiveCombo } from '../../components/hotkeys';
import { BusyIcon } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { guardedExit, resetUnsavedTracking, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { useNavActions } from '../../hooks/useNavActions';
import { fmtLocalDate } from '../../utils/datetime';
import { ParameterPickerDialog } from '../modules/ParameterPickerDialog';
import { ContractLabTestPickerDialog } from './ContractLabTestPickerDialog';
import {
  CLP_BASE, CLP_FORM_NAME, CLP_PAGE_ID, SELECT_CHECKBOX,
  changedClpRows, filterClpRows, firstClpProblem, newClpRow, priceText, sortClpRows, toClpInput, toClpRow,
  type ClpEditable, type ClpOption, type ClpRow, type ClpTestPick,
} from './contractLabTestPricing';
import './contractLabTestPricing.css';

/**
 * Settings ▸ Test Pricing ▸ Contract Lab Test Pricing — the G5 batch-edit list
 * (ContractLabTestPricing_ListView, InlineEditMode="Batch", no New / Edit / Delete;
 * actions from SuboutPOController). Bar, in G5's order (Search on its own row below):
 * **Refresh · Add · Remove · Save · Export to · Search**.
 *
 * - **Add** opens the Tests picker; each ticked test becomes an unsaved row.
 * - TAT, Unit Price, Contract Lab and Comment are edited in the grid.
 * - **Save** stores every added and edited row in one transaction; added rows get
 *   their "CP" price code there.
 * - **Remove** removes the ticked prices, keeping any a subout test order uses.
 * - Double-clicking a saved row's **Parameter** cell opens the parameter picker,
 *   which saves at once (as G5 does).
 *
 * No detail view, as in G5, so this is a bespoke page built from the shared grid,
 * toolbar, title and save / delete flows (Scientific Data Table precedent).
 */
export function ContractLabTestPricingPage({ onHome }: { onHome: () => void }) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  // MAC rights: Add = Create, Remove = Delete, Save + in-grid editing + Parameter picker = Write
  // (Save is also kept for a Create-only role so it can store the rows it added). Not-held actions are hidden.
  const perm = useNavActions(CLP_PAGE_ID);
  const canEdit = perm.write || perm.create;

  const [rows, setRows] = useState<ClpRow[]>([]);
  const [loaded, setLoaded] = useState<Map<string, ClpRow>>(new Map());
  const [tats, setTats] = useState<ClpOption[]>([]);
  const [labs, setLabs] = useState<ClpOption[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [picking, setPicking] = useState(false);
  const [paramRow, setParamRow] = useState<ClpRow | null>(null);
  // Remounts the grid so added rows are on screen: page 1, no column filter hiding them.
  const [gridKey, setGridKey] = useState(0);
  const seq = useRef(0);

  const changed = useMemo(() => changedClpRows(rows, loaded), [rows, loaded]);
  const dirty = changed.length > 0;
  // Rule 15: the grid buffer is state the automatic tracker cannot see.
  useUnsavedGuard(dirty);

  const load = useCallback(async () => {
    const started = performance.now();
    setLoading(true);
    try {
      const [list, t, l] = await Promise.all([
        api.get<{ data?: Record<string, unknown>[] }>(`/api/v1/modules/${CLP_PAGE_ID}`, { params: { page: 1, pageSize: 10000 } }),
        api.get<{ data?: ClpOption[] }>(`${CLP_BASE}/tats`),
        api.get<{ data?: ClpOption[] }>(`${CLP_BASE}/contract-labs`),
      ]);
      const next = sortClpRows((list.data?.data ?? []).map(toClpRow));
      setRows(next);
      setLoaded(new Map(next.map((r) => [r.key, r])));
      setTats(t.data?.data ?? []);
      setLabs(l.data?.data ?? []);
      setSelected(new Set());
      resetUnsavedTracking();
    } catch {
      // An empty grid must not pass for "no prices" when the list could not be read.
      notify.error('The contract lab test prices could not be loaded. Check that the server is running, then press Refresh.');
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - started);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const patch = (key: string, p: Partial<ClpEditable>) =>
    setRows((d) => d.map((r) => (r.key === key ? { ...r, ...p } : r)));

  // ── Add: the Tests picker; one unsaved row per ticked test ─────────────────
  const addTests = (tests: ClpTestPick[]) => {
    const added = tests.map((t) => { seq.current += 1; return newClpRow(seq.current, t); });
    setRows((d) => [...added, ...d]);
    setPicking(false);
    setSearch('');
    setGridKey((k) => k + 1);
  };

  // ── Save: every added / edited row, one transaction ────────────────────────
  const save = () => {
    if (!dirty || saving) return;
    const problem = firstClpProblem(changed);
    if (problem) { notify.warning(problem); return; }
    void runSave({
      formName: CLP_FORM_NAME,
      // A reason is asked for (when configured) only if a stored row was changed.
      auditAction: changed.some((r) => r.id != null) ? 'edit' : 'create',
      save: async () => {
        setSaving(true);
        try {
          await api.post(`${CLP_BASE}/save`, { rows: changed.map((r) => toClpInput(r, tats, labs)) });
        } finally {
          setSaving(false);
        }
      },
      onSaved: () => { void load(); },
      onError: (e) => {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
        notify.error(msg ?? 'The prices could not be saved.');
      },
    });
  };

  // ── Remove: the ticked rows (G5 SuboutPORemove) ─────────────────────────────
  const remove = () => {
    if (selected.size === 0) { notify.info(SELECT_CHECKBOX); return; }
    const picked = rows.filter((r) => selected.has(r.key));
    const stored = picked.filter((r) => r.id != null);
    // Takes rows out of the grid (and of the load snapshot) without touching the others' unsaved edits.
    const drop = (keys: Set<string>) => {
      setRows((d) => d.filter((r) => !keys.has(r.key)));
      setLoaded((m) => { const n = new Map(m); keys.forEach((k) => n.delete(k)); return n; });
      setSelected(new Set());
    };
    const unsavedKeys = new Set(picked.filter((r) => r.id == null).map((r) => r.key));
    // Rows never saved are simply taken out of the grid.
    if (stored.length === 0) { drop(unsavedKeys); return; }

    let outcome: { removedIds?: string[]; level?: string; message?: string } = {};
    void runDelete({
      formName: CLP_FORM_NAME,
      message: stored.length === 1
        ? `Remove price code '${stored[0]!.priceCode || stored[0]!.test}'?`
        : `Remove ${stored.length} price codes?`,
      delete: async () => {
        const res = await api.post<{ data?: typeof outcome }>(`${CLP_BASE}/remove`, { ids: stored.map((r) => r.id) });
        outcome = res.data?.data ?? {};
      },
      successMessage: null,
      onDeleted: () => {
        // G5's own wording for each outcome (all removed / some in use / all in use).
        const msg = outcome.message ?? 'Deleted successfully.';
        if (outcome.level === 'error') notify.error(msg);
        else if (outcome.level === 'warning') notify.warning(msg);
        else notify.success(msg);
        // The removed prices (never the in-use ones) and the unsaved ticked rows leave the grid.
        const removed = new Set((outcome.removedIds ?? []).map((id) => id.toLowerCase()));
        drop(new Set([...unsavedKeys, ...stored.filter((r) => removed.has(r.id!.toLowerCase())).map((r) => r.key)]));
      },
    });
  };

  const refresh = () => { if (dirty) void guardedExit(() => { void load(); }); else void load(); };

  // ── Parameter: double-click a saved row's cell (G5 ondblclick → picker) ─────
  const openParameters = (r: ClpRow) => {
    if (!perm.write) return;
    if (r.id == null) { notify.info('Save the row before choosing its parameters.'); return; }
    setParamRow(r);
  };
  const applyParameters = async (r: ClpRow, ids: string[], names: Record<string, string>) => {
    setParamRow(null);
    try {
      await api.put(`${CLP_BASE}/${r.id}/parameters`, { ids });
      const text = ids.map((id) => names[id] ?? id).join(', ');
      // Saved at once, so both the row and its loaded copy take the new value (not an unsaved edit).
      setRows((d) => d.map((x) => (x.key === r.key ? { ...x, parameters: text } : x)));
      setLoaded((m) => { const n = new Map(m); const o = n.get(r.key); if (o) n.set(r.key, { ...o, parameters: text }); return n; });
      notify.success('Saved successfully.');
    } catch {
      notify.error('The parameters could not be saved.');
    }
  };

  // Save is a form-specific button, so its key is registered here (rule 14).
  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: 'List', order: 1, run: () => save(), disabled: !canEdit || !dirty || saving },
  ]);

  const tatNames = useMemo(() => tats.map((o) => o.name), [tats]);
  const labNames = useMemo(() => labs.map((o) => o.name), [labs]);

  // G5 ContractLabTestPricing_ListView columns and captions, in its order.
  const columns: AppColumn<ClpRow>[] = useMemo(() => [
    { field: 'priceCode', header: 'PriceCode', width: 110, minWidth: 110 },
    { field: 'matrix', header: 'Matrix', width: 110, minWidth: 110 },
    { field: 'test', header: 'Test', width: 170, minWidth: 170 },
    { field: 'method', header: 'Method', width: 130, minWidth: 130 },
    { field: 'parameters', header: 'Parameter', width: 360, minWidth: 360,
      render: (r) => (
        <span
          className="clp-param"
          title={!perm.write ? undefined : r.id == null ? 'Save the row first' : 'Double-click to choose the parameters'}
          onDoubleClick={perm.write ? (e) => { e.stopPropagation(); openParameters(r); } : undefined}
        >
          {r.parameters || ' '}
        </span>
      ) },
    { field: 'tat', header: 'TAT', width: 110, minWidth: 110,
      edit: canEdit ? { type: 'combo', options: tatNames, requiredEmpty: (r) => !r.tat.trim() } : undefined },
    { field: 'unitPrice', header: 'UnitPrice', width: 100, minWidth: 100, align: 'right',
      text: (r) => priceText(r.unitPrice), edit: canEdit ? { type: 'number', display: priceText } : undefined },
    { field: 'contractLab', header: 'ContractLab', width: 150, minWidth: 150,
      edit: canEdit ? { type: 'combo', options: labNames, requiredEmpty: (r) => !r.contractLab.trim() } : undefined },
    { field: 'modifiedDate', header: 'LastUpdatedDate', width: 130, minWidth: 130,
      value: (r) => r.modifiedDate, text: (r) => (r.modifiedDate ? fmtLocalDate(r.modifiedDate) : '') ,
      render: (r) => (r.modifiedDate ? fmtLocalDate(r.modifiedDate) : '') },
    { field: 'changePercent', header: 'Change%', width: 90, minWidth: 90 },
    { field: 'comment', header: 'Comment', edit: canEdit ? { type: 'text' } : undefined },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [tatNames, labNames, canEdit, perm.write]);

  const shown = useMemo(() => filterClpRows(rows, search), [rows, search]);

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className="dp-breadcrumb">
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          <span>Test Pricing</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Contract Lab Test Pricing</span>
        </div>

        <FormTitle pageId={CLP_PAGE_ID} loadMs={loadMs} loading={loading} auditFormName={CLP_FORM_NAME} />

        <div className="clp-panel">
          {/* G5 bar: Refresh · Add · Remove · Save · Export to · Search. */}
          <AppToolbar
            refreshFirst
            crudFirst
            onRefresh={refresh}
            refreshing={loading}
            newLabel="Add"
            onNew={perm.create ? () => setPicking(true) : undefined}
            newDisabled={loading || saving}
            deleteLabel="Remove"
            onDelete={perm.delete ? remove : undefined}
            deleteDisabled={saving}
            left={canEdit ? (
              <button
                className="ca-btn ca-btn-solid"
                onClick={save}
                disabled={!dirty || saving}
                title={`Save (${formatCombo(effectiveCombo('mod+s'))})`}
              >
                {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
              </button>
            ) : undefined}
            exportData={{ filename: 'Contract Lab Test Pricing', ...gridExportMatrix(shown, columns) }}
          />

          {/* Search on its own row under the bar, like every list form (owner request 2026-10-14). */}
          <div className="list-search-row clp-search-row" data-unsaved-ignore>
            {/* The standard list search box (same markup as every list form). */}
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input
                type="text"
                placeholder="Search…"
                aria-label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
            {dirty && (
              <span className="clp-pending">
                {changed.length} unsaved {changed.length === 1 ? 'row' : 'rows'}
              </span>
            )}
          </div>

          <AppDataGrid<ClpRow>
            key={gridKey}
            rows={shown}
            columns={columns}
            getRowId={(r) => r.key}
            loading={loading}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            onCellCommit={(row, col, v) => patch(row.key, { [col.field]: v == null ? '' : String(v) } as Partial<ClpEditable>)}
            onCellChange={(row, col, v) => patch(row.key, { [col.field]: v == null ? '' : String(v) } as Partial<ClpEditable>)}
            getRowClassName={(r) => (r.id == null ? 'clp-row-new' : undefined)}
            defaultPageSize={20}
            exportFilename="Contract Lab Test Pricing"
            emptyText={perm.create ? 'No prices — use Add to create them' : 'No prices'}
          />
        </div>
      </div>

      {picking && <ContractLabTestPickerDialog onClose={() => setPicking(false)} onPicked={addTests} />}
      {paramRow && (
        <ParameterPickerDialog
          title="Parameter"
          optionsUrl={`${CLP_BASE}/${paramRow.id}/parameter-options`}
          onClose={() => setParamRow(null)}
          onApply={(ids, names) => { void applyParameters(paramRow, ids, names); }}
        />
      )}
    </div>
  );
}
